/**
 * Load-failure states for the Library and the sharing sheets (de-slop
 * CHAT-10). A failed request must read as "couldn't load, try again", never
 * as an empty library or an empty recipient list.
 */
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system/theme/reviewDesk';

const ERROR_RED = '#B42318';

interface ErrorStateProps {
  title: string;
  body?: string;
  retryLabel: string;
  onRetry: () => void;
  testID?: string;
}

/** Full-size error card that replaces a list which failed to load. */
export function LibraryErrorState({ title, body, retryLabel, onRetry, testID }: ErrorStateProps) {
  return (
    <View style={styles.state} testID={testID} accessibilityRole="alert">
      <View style={styles.stateIcon}>
        <MaterialIcons name="cloud-off" size={32} color={ERROR_RED} />
      </View>
      <Text style={styles.stateTitle}>{title}</Text>
      {!!body && <Text style={styles.stateBody}>{body}</Text>}
      <TouchableOpacity style={styles.retryButton} onPress={onRetry} accessibilityRole="button" testID={testID ? `${testID}_retry` : undefined}>
        <MaterialIcons name="refresh" size={18} color={reviewDesk.accent} />
        <Text style={styles.retryText}>{retryLabel}</Text>
      </TouchableOpacity>
    </View>
  );
}

interface ErrorBannerProps {
  message: string;
  retryLabel: string;
  onRetry: () => void;
  testID?: string;
}

/** Inline banner when some rows are on screen but a refresh / next page failed. */
export function LibraryErrorBanner({ message, retryLabel, onRetry, testID }: ErrorBannerProps) {
  return (
    <View style={styles.banner} testID={testID} accessibilityRole="alert">
      <MaterialIcons name="error-outline" size={18} color={ERROR_RED} />
      <Text style={styles.bannerText}>{message}</Text>
      <TouchableOpacity onPress={onRetry} accessibilityRole="button" style={styles.bannerRetry} testID={testID ? `${testID}_retry` : undefined}>
        <Text style={styles.retryText}>{retryLabel}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  state: {
    minHeight: 260,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 10,
  },
  stateIcon: {
    width: 64,
    height: 64,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FEF3F2',
  },
  stateTitle: {
    color: reviewDesk.ink,
    fontSize: 17,
    fontWeight: '800',
    textAlign: 'center',
  },
  stateBody: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 420,
  },
  retryButton: {
    marginTop: 6,
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  retryText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#FECDCA',
    backgroundColor: '#FEF3F2',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginVertical: 8,
  },
  bannerText: {
    flex: 1,
    minWidth: 0,
    color: ERROR_RED,
    fontSize: 13,
    lineHeight: 18,
  },
  bannerRetry: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
});
